import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useActiveBusiness } from '@/hooks/useCurrentActiveBusiness'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_dashboard/billing')({
  component: RouteComponent,
  beforeLoad: async () => {
    await queryClient.ensureQueryData(
      orpc.usage.getBusinessUsage.queryOptions(),
    )
  },
})

function RouteComponent() {
  const usageInfo = useQuery(orpc.usage.getBusinessUsage.queryOptions())
  const { activeBusiness } = useActiveBusiness()

  const startCheckout = useMutation(
    orpc.subscription.startSubscriptionCheckout.mutationOptions({
      onSuccess: (data) => {
        // redirect user to url
        if (data) {
          window.location.href = data
        }
      },
    }),
  )

  const startManageSession = useMutation(
    orpc.subscription.startSubscriptionManagementSession.mutationOptions({
      onSuccess: (data) => {
        if (data) {
          window.location.href = data
        }
      },
    }),
  )

  const hasSubscriptionAccess = usageInfo.data?.hasSubscriptionAccess
  const subscriptionStatus = usageInfo.data?.subscription.status
  const cancelAtPeriodEnd = usageInfo.data?.subscription.cancelAtPeriodEnd
  const cancelAt = usageInfo.data?.subscription.cancelAt
  const billingStart = usageInfo.data?.subscription.billingPeriodStart
  const billingEnd = usageInfo.data?.subscription.billingPeriodEnd

  const formatDate = (input?: string | Date | null) => {
    if (!input) return '—'
    try {
      const d = input instanceof Date ? input : new Date(input)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch (e) {
      return '—'
    }
  }

  // Mocked billing data
  const currentPlan = {
    name: 'Pro',
    price: 49,
    interval: 'month',
  }

  const usage = {
    articlesThisMonth:
      usageInfo.data?.metrics['article-generation']?.totalQuantity ?? 0,
    articleLimit: usageInfo.data?.subscription.articleGenLimit ?? 0,

    imagesThisMonth:
      usageInfo.data?.metrics['image-generation']?.totalQuantity ?? 0,
    imageLimit: usageInfo.data?.subscription.imageGenLimit ?? 0,
  }

  const percent = (value: number, total: number) =>
    Math.min(100, Math.round((value / total) * 100))

  return (
    <div className="mx-auto max-w-7xl p-8 space-y-6">
      <PageHeader
        title="Billing"
        description={`Manage your plan and view your usage`}
      />
      <div className="gap-6 rounded-xl">
        {/* Left: Plan & Usage */}
        <div className="space-y-6 lg:col-span-2">
          {!hasSubscriptionAccess ? (
            <Card className="overflow-hidden pt-0">
              <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-500 p-6 text-white">
                <div className="flex items-start gap-4">
                  <div>
                    <h3 className="text-xl font-semibold">
                      You're not subscribed for {activeBusiness?.businessName}
                    </h3>
                    <p className="mt-1 text-sm opacity-90">
                      Start your 5-day free trial to unlock all features for
                      this business.
                    </p>
                  </div>
                </div>
              </div>

              <CardContent className="-mt-4">
                <div className="rounded-lg bg-white p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-green-500 mt-1 flex-shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <div className="font-medium">
                          AI-powered article generation
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Create SEO-optimized articles with advanced AI
                          assistance
                        </div>
                      </div>
                    </li>

                    <li className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-green-500 mt-1 flex-shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <div className="font-medium">
                          Custom image generation
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Produce unique imagery for articles and social media
                          posts
                        </div>
                      </div>
                    </li>

                    <li className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-green-500 mt-1 flex-shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <div className="font-medium">
                          Automated keyword research & tracking
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Our AI will do automated keyword research to write
                          relevant, SEO-focused articles
                        </div>
                      </div>
                    </li>

                    <li className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-green-500 mt-1 flex-shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <div className="font-medium">
                          Content calendar & scheduling
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Plan and organize your content strategy effectively
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <div className="flex flex-wrap gap-3">
                      <Button
                        className="shadow"
                        disabled={startCheckout.isPending}
                        onClick={() => startCheckout.mutate({})}
                      >
                        {startCheckout.isPending
                          ? 'Starting Checkout...'
                          : 'Start 5-Day Free Trial'}
                      </Button>
                      <Button
                        className="shadow"
                        variant="outline"
                        onClick={() => startManageSession.mutate({})}
                      >
                        Manage Subscriptions
                      </Button>
                    </div>
                    <span className="ml-auto text-sm text-muted-foreground">
                      Cancel anytime • No credit card required
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader className="flex justify-between">
                  <div className="space-y-1">
                    <CardTitle>Current Plan</CardTitle>
                    <CardDescription>
                      Your active subscription and benefits for{' '}
                      {activeBusiness?.businessName}.
                    </CardDescription>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => startManageSession.mutate({})}>
                      Manage subscription
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-semibold">
                          ${currentPlan.price}/{currentPlan.interval}
                        </span>
                        <Badge>{currentPlan.name}</Badge>
                      </div>

                      {/* Subscription status and billing period */}
                      <div className="text-sm text-muted-foreground mt-1">
                        <div>
                          <strong>Status:</strong>{' '}
                          <span className="capitalize">
                            {subscriptionStatus}
                          </span>
                        </div>

                        {cancelAtPeriodEnd ? (
                          <div className="text-amber-600">
                            Will cancel at period end
                          </div>
                        ) : null}

                        {subscriptionStatus === 'trialing' && (
                          <div className="mt-1">
                            <strong>Trial ends:</strong>{' '}
                            {formatDate(usageInfo.data?.subscription.trialEnd)}
                          </div>
                        )}

                        {!cancelAt ? (
                          <div className="mt-1">
                            <strong>Billing period:</strong>{' '}
                            {formatDate(billingStart)} —{' '}
                            {formatDate(billingEnd)}
                          </div>
                        ) : (
                          <div className="mt-1">
                            <strong>Ends: </strong> {formatDate(billingEnd)}{' '}
                            (Will not renew)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Usage</CardTitle>
                  <CardDescription>
                    Current cycle usage against your limits
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <UsageRow
                    label="Articles generated"
                    value={usage.articlesThisMonth}
                    limit={usage.articleLimit}
                    percent={percent(
                      usage.articlesThisMonth,
                      usage.articleLimit,
                    )}
                  />
                  <UsageRow
                    label="Images generated"
                    value={usage.imagesThisMonth}
                    limit={usage.imageLimit}
                    percent={percent(usage.imagesThisMonth, usage.imageLimit)}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function UsageRow({
  label,
  value,
  limit,
  percent,
  unit,
}: {
  label: string
  value: number
  limit: number
  percent: number
  unit?: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {value}
          {unit ? ` ${unit}` : ''} / {limit}
          {unit ? ` ${unit}` : ''}
        </span>
      </div>
      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}
